import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import StripeElementsDialog from "../components/StripeElementsDialog";
import QuoteRequestDialog from "../components/QuoteRequestDialog";
import ManualPaymentDialog from "../components/ManualPaymentDialog";
import ProvenancePanel from "../components/ProvenancePanel";
import OrderActions, { MIN_USD } from "../components/OrderActions";
import usePricing from "../hooks/usePricing";
import { api, fmtMoney, convert, formatApiError, CURRENCIES } from "../lib/api";
import { useCurrency } from "../contexts/CurrencyContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const QUICK_USD = [100_000, 250_000, 500_000, 1_000_000];

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { currency: globalCur } = useCurrency();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [params, setParams] = useSearchParams();
  const [showElements, setShowElements] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showSwift, setShowSwift] = useState(false);
  const [showUsdt, setShowUsdt] = useState(false);

  const pricing = usePricing({ initialPayCur: globalCur, initialTons: 1 });
  const { tons, amount, payCur, indexUsd, totalUsd, setTons, setAmount, setPayCur, setLastEdited } = pricing;

  useEffect(() => { setPayCur(globalCur); }, [globalCur, setPayCur]);

  useEffect(() => {
    if (params.get("cancelled")) {
      toast.error(t("pd.toast_card_cancelled"));
      setParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => nav("/marketplace"));
    if (user) api.get("/wallet").then((r) => setWallet(r.data)).catch(() => {});
  }, [id, user, nav]);

  if (!p) return <Layout><div className="max-w-7xl mx-auto p-10 text-ink2">{t("pd.loading")}</div></Layout>;

  const totalLocal = convert(totalUsd, payCur);
  const balance = wallet?.balances?.[payCur] || 0;
  const minAmount = Number(convert(MIN_USD, payCur).toFixed(2));
  const minTons = Number((MIN_USD / indexUsd).toFixed(4));

  const buy = async () => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/trade/buy", { project_id: id, tons: Number(tons), currency: payCur });
      toast.success(t("pd.toast_purchased", { tons: data.transaction.tons }));
      setWallet((w) => ({ ...w, ucs_tons: data.ucs_tons, balances: { ...w.balances, [payCur]: data.balance } }));
      setP((proj) => ({ ...proj, tons_available: proj.tons_available - data.transaction.tons }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const buyWithCard = async () => {
    if (!user) { nav("/login"); return; }
    if (payCur !== "USD" && payCur !== "EUR") {
      toast.error(t("pd.toast_card_usd_eur_only"));
      return;
    }
    if (Number(tons) <= 0) { toast.error(t("pd.toast_enter_qty")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout/ucs", {
        project_id: id, tons: Number(tons), currency: payCur, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const desc = p.description_key ? t(`projects.${p.description_key}.description`, { defaultValue: p.description }) : p.description;
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    description: desc,
    image: p.image_url,
    brand: { "@type": "Organization", name: p.certifier || "BMV Global" },
    category: p.type,
    identifier: p.b3_isin || p.bmv_unit_id || p.id,
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: p.price_usd_per_ton,
      availability: (p.tons_available || 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      url: `${window.location.origin}/project/${p.id}`,
    },
  };

  return (
    <Layout>
      <SEO
        title={`${p.name} — Verified UCS Carbon Project`}
        description={`${p.name} · ${p.region} · ${p.type}. ${p.tons_available.toLocaleString()} tCO₂ available at ~$${p.price_usd_per_ton.toFixed(2)}/ton. Audited by ${p.audit_firm || p.certifier}. B3 ISIN: ${p.b3_isin || "pending"}.`}
        path={`/project/${p.id}`}
        image={p.image_url}
        type="product"
        jsonLd={productJsonLd}
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10">
        <Link to="/marketplace" className="text-xs font-mono text-ink2 hover:text-ink" data-testid="back-link">{t("pd.back")}</Link>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="aspect-[16/10] overflow-hidden border border-line">
            <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
          </div>
          <div className="mt-8">
            <div className="label-tag">{p.type} · {p.region}</div>
            <h1 className="font-serif text-4xl lg:text-5xl mt-2 tracking-tight" data-testid="project-name">{p.name}</h1>
            <p className="text-ink2 mt-5 leading-relaxed max-w-2xl" data-testid="project-description">{desc}</p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-line border border-line mt-8">
              {[
                [t("pd.certifier"), p.certifier],
                [t("pd.vintage"), p.vintage],
                [t("pd.available"), `${p.tons_available.toLocaleString()} t`],
                [t("pd.estimate_price"), `$${p.price_usd_per_ton.toFixed(2)} / t`],
              ].map(([k, v]) => (
                <div key={k} className="bg-surface p-5">
                  <div className="label-tag">{k}</div>
                  <div className="mono-num text-lg mt-1">{v}</div>
                </div>
              ))}
            </div>

            <ProvenancePanel p={p} />
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="border border-ink p-8 sticky top-24">
            <div className="label-tag">{t("pd.order_ticket")}</div>
            <div className="font-serif text-3xl mt-1">{t("pd.buy")} {p.name.split(" ").slice(0, 2).join(" ")}</div>

            <div className="mt-8 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-tag block mb-2">{t("pd.tons_label")}</label>
                  <input
                    data-testid="buy-tons"
                    type="number"
                    min={minTons}
                    step="0.001"
                    value={tons}
                    onChange={(e) => { setLastEdited("tons"); setTons(e.target.value); }}
                    className="input-field"
                  />
                  <div className="text-[10px] font-mono text-ink2 mt-1">{t("pd.min_tons", { value: Number(minTons).toLocaleString(undefined, { maximumFractionDigits: 3 }) })}</div>
                </div>
                <div>
                  <label className="label-tag block mb-2">{t("pd.total_label", { cur: payCur })}</label>
                  <input
                    data-testid="buy-amount"
                    type="number"
                    min={minAmount}
                    step="100"
                    value={amount}
                    onChange={(e) => { setLastEdited("amount"); setAmount(e.target.value); }}
                    placeholder={fmtMoney(minAmount, payCur)}
                    className="input-field"
                  />
                  <div className="text-[10px] font-mono text-ink2 mt-1">{t("pd.min_amount", { value: fmtMoney(minAmount, payCur) })}</div>
                </div>
              </div>

              <div>
                <label className="label-tag block mb-2">{t("pd.pay_with")}</label>
                <select data-testid="buy-currency" value={payCur} onChange={(e) => setPayCur(e.target.value)} className="input-field">
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="flex items-center gap-1 flex-wrap">
                <span className="label-tag mr-1">{t("pd.quick_fill")}</span>
                {QUICK_USD.map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => { setLastEdited("amount"); setAmount(convert(u, payCur).toFixed(2)); }}
                    data-testid={`preset-${u}`}
                    className="px-2 py-1 text-[10px] font-mono uppercase border border-line text-ink2 hover:border-ink hover:text-ink"
                  >
                    {u >= 1_000_000 ? `${u / 1_000_000}M` : `${u / 1_000}k`} USD
                  </button>
                ))}
              </div>
              <div className="pt-5 border-t border-line space-y-2">
                {Number(tons || 0) > 0 && (
                  <div className="bg-ink/[0.04] border border-ink/20 px-3 py-2 -mt-2 mb-2" data-testid="charge-banner">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-ink2">{t("pd.you_will_be_charged")}</div>
                    <div className="font-serif text-lg text-ink leading-tight mt-0.5">
                      {fmtMoney(totalLocal, payCur)}
                      {payCur !== "USD" && (
                        <span className="text-[11px] font-mono text-ink2 ml-2">≈ {fmtMoney(totalUsd, "USD")}</span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-ink2 mt-0.5">
                      {t("pd.for_tons_settled_in", { tons: Number(tons || 0).toLocaleString(undefined, { maximumFractionDigits: 3 }), cur: payCur })}
                    </div>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-ink2">{t("pd.unit_price")}</span>
                  <span className="mono-num">{fmtMoney(convert(indexUsd, payCur), payCur)}<span className="text-[10px] text-ink2 ml-1">{t("pd.ucs_index")}</span></span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-ink2">{t("pd.tons")}</span>
                  <span className="mono-num">{Number(tons || 0).toFixed(3)}</span>
                </div>
                <div className="flex justify-between text-base pt-2 border-t border-line">
                  <span className="font-serif">{t("pd.total")}</span>
                  <span className="mono-num text-xl" data-testid="buy-total">{fmtMoney(totalLocal, payCur)}</span>
                </div>
                <div className="flex justify-between text-xs text-ink2">
                  <span>{t("pd.usd_equivalent")}</span>
                  <span className="mono-num" data-testid="buy-total-usd">{fmtMoney(totalUsd, "USD")}</span>
                </div>
                {user && (
                  <div className="flex justify-between text-xs text-ink2 pt-1">
                    <span>{t("pd.wallet_balance", { cur: payCur })}</span>
                    <span className="mono-num">{fmtMoney(balance, payCur)}</span>
                  </div>
                )}
              </div>

              <OrderActions
                user={user}
                busy={busy}
                tons={tons}
                totalUsd={totalUsd}
                payCur={payCur}
                indexUsd={indexUsd}
                projectId={id}
                nav={nav}
                onBuy={buy}
                onCardElements={() => setShowElements(true)}
                onHostedCard={buyWithCard}
                onSwift={() => setShowSwift(true)}
                onUsdt={() => setShowUsdt(true)}
                onQuote={() => setShowQuote(true)}
              />

              {user && balance < totalLocal && (
                <Link to="/wallet" className="block text-center text-xs text-ink2 underline mt-1" data-testid="buy-deposit-link">
                  {t("pd.insufficient_deposit")}
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      <StripeElementsDialog
        open={showElements}
        onClose={() => setShowElements(false)}
        projectId={id}
        projectName={p?.name}
        tons={tons}
        currency={payCur}
        onSuccess={() => setP((proj) => proj ? { ...proj, tons_available: proj.tons_available - Number(tons) } : proj)}
      />
      <QuoteRequestDialog
        open={showQuote}
        onClose={() => setShowQuote(false)}
        projectId={id}
        projectName={p?.name}
        tons={tons}
        currency={payCur}
        totalUsd={totalUsd}
        unitPriceUsd={indexUsd}
      />
      <ManualPaymentDialog open={showSwift} method="swift" onClose={() => setShowSwift(false)} projectId={id} projectName={p?.name} tons={tons} currency={payCur} />
      <ManualPaymentDialog open={showUsdt} method="usdt" onClose={() => setShowUsdt(false)} projectId={id} projectName={p?.name} tons={tons} currency={payCur} />
    </Layout>
  );
}
